import { Router } from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { parseConvertedChildrenFromApiResponse } from './document-conversion-lock.util';
import {
  DocumentsLieSkeletonRow,
  enrichDocumentsLieSkeletonRows$,
} from './document-linked-documents.util';
import { DocumentService } from './document.service';
import { extractLogicielCaisseTransfertId } from './bon-reception-lc-origin.util';
import { readPvEcartLcTransfertIdFromDoc } from './pv-ecart-regularisation-flow.util';
import {
  readPvEcartLcTransfertNumFromDoc,
  resolvePvEcartLcReservationReference$,
} from './pv-ecart-stock-workflow.util';
import { resolveTransferWorkflowRootId } from './stock-transfer-workflow.util';
import { STOCK_TRANSFER_WORKFLOW_TYPE_IDS } from './stock-transfer-workflow.constants';
import { LogicielCaisseStockTransfertService } from '../logiciel-caisse/logiciel-caisse-stock-transfert.service';
import { pickLcTransfertPrimaryPhaseId } from '../logiciel-caisse/logiciel-caisse-stock-transfert-modal-detail.util';
import { resolvePvEcartBonTransfertWorkflowRootId } from './pv-ecart-bon-transfert-conversion.util';
import {
  erpLinkedRefToDisplayCode,
  lookupStockTransfertErpLinkedRefsEntry,
  StockTransfertErpLinkedRefsItem,
} from './stock-transfert-erp-linked-refs.util';
import type { LcReceptionErpWorkflowLinkRow } from './stock-transfert-erp-linked-refs.util';

export type PvEcartLinkedDocRow = {
  label: string;
  code: string;
  /** Lien ERP ou LC (si défini). */
  link?: { path: string; queryParams?: Record<string, string> };
};

export type PvEcartLinkedDocumentsView = {
  transfert: PvEcartLinkedDocRow;
  bonSortie: PvEcartLinkedDocRow;
  bonReception: PvEcartLinkedDocRow;
};

const EMPTY_ROW = (label: string): PvEcartLinkedDocRow => ({
  label,
  code: '—',
});

function readMergedHidden(doc: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const sources = [
    doc['hidden_client_fields'],
    doc['hidden_fournisseur_fields'],
    (doc['fullData'] as Record<string, unknown> | undefined)?.['hidden_client_fields'],
    (doc['fullData'] as Record<string, unknown> | undefined)?.['hidden_fournisseur_fields'],
  ];
  for (const src of sources) {
    if (src && typeof src === 'object') {
      Object.assign(out, src as Record<string, unknown>);
    }
  }
  return out;
}

export function formatErpDocumentCode(
  doc: unknown,
  options?: { allowIdFallback?: boolean }
): string {
  if (!doc || typeof doc !== 'object') {
    return '—';
  }
  const d = doc as Record<string, unknown>;
  const ref = String(
    d['ref_externe'] ?? d['reference'] ?? d['ref_interne'] ?? d['ref_externe_document'] ?? ''
  ).trim();
  if (ref) {
    return ref;
  }
  if (options?.allowIdFallback === false) {
    return '—';
  }
  const id = Number(d['id']);
  return Number.isFinite(id) && id > 0 ? `#${id}` : '—';
}

export function isPvEcartPlaceholderDocumentCode(code: unknown): boolean {
  const c = String(code ?? '').trim();
  return !c || c === '—' || /^#\d+$/.test(c);
}

/** Affichage liste : num_document LC, pas l’id phase (#123). */
export function normalizeLcStockTransfertDisplayCode(
  code?: string | null,
  numDocument?: string | null
): string {
  const num = String(numDocument ?? '').trim();
  if (num) {
    return num;
  }
  const c = String(code ?? '').trim();
  if (c && !isPvEcartPlaceholderDocumentCode(c)) {
    return c;
  }
  return '—';
}

/** Référence « Document de transfert » (Documents liés) pour l’en-tête ERP / transfert depuis PV. */
export function resolvePvEcartLinkedTransfertHeaderReference(
  pvDoc: Record<string, unknown> | null | undefined,
  view: PvEcartLinkedDocumentsView | null | undefined
): string {
  if (pvDoc) {
    const hidden = readMergedHidden(pvDoc);
    const stockNum = String(hidden['stock_transfert_num_document'] ?? '').trim();
    if (stockNum) {
      return stockNum;
    }
    const fromHiddenLc = readPvEcartLcTransfertNumFromDoc(pvDoc);
    if (fromHiddenLc) {
      return fromHiddenLc;
    }
  }
  const fromPanel = String(view?.transfert?.code ?? '').trim();
  if (fromPanel && !isPvEcartPlaceholderDocumentCode(fromPanel)) {
    return fromPanel;
  }
  return '';
}

function pickLatestChildByType(children: any[], typeId: number): any | null {
  const list = (children || []).filter((c) => Number(c?.type_document_id) === typeId);
  if (!list.length) {
    return null;
  }
  return list.sort((a, b) => Number(b?.id) - Number(a?.id))[0];
}

function resolveBonTransfertFromWorkflow(
  rootDoc: unknown,
  children: any[]
): Record<string, unknown> | null {
  if (rootDoc && typeof rootDoc === 'object') {
    const d = rootDoc as Record<string, unknown>;
    const typeId = Number(d['type_document_id'] ?? (d['typeDocument'] as { id?: number })?.id);
    if (typeId === STOCK_TRANSFER_WORKFLOW_TYPE_IDS.BON_TRANSFERT) {
      return d;
    }
  }
  const fromChildren = pickLatestChildByType(children, STOCK_TRANSFER_WORKFLOW_TYPE_IDS.BON_TRANSFERT);
  return fromChildren ?? null;
}

function extractLcTransfertIdFromPvDoc(doc: Record<string, unknown>): number | null {
  const fromPvKeys = readPvEcartLcTransfertIdFromDoc(doc);
  if (fromPvKeys != null) {
    return fromPvKeys;
  }
  const erpId = Number(doc['id']);
  return extractLogicielCaisseTransfertId(
    doc,
    Number.isFinite(erpId) && erpId > 0 ? erpId : null
  );
}

function readLcTransfertIdFromWorkflowDocs(
  pvDoc: Record<string, unknown>,
  rootDoc: unknown,
  children: any[]
): number | null {
  const candidates: Record<string, unknown>[] = [pvDoc];
  const bt = resolveBonTransfertFromWorkflow(rootDoc, children);
  if (bt) {
    candidates.push(bt);
  }
  const pvChild = pickLatestChildByType(children, STOCK_TRANSFER_WORKFLOW_TYPE_IDS.PV_ECART);
  if (pvChild && typeof pvChild === 'object') {
    candidates.push(pvChild as Record<string, unknown>);
  }
  for (const c of candidates) {
    const id =
      readPvEcartLcTransfertIdFromDoc(c) ??
      extractLogicielCaisseTransfertId(
        c,
        Number.isFinite(Number(c['id'])) && Number(c['id']) > 0 ? Number(c['id']) : null
      );
    if (id != null && id > 0) {
      return id;
    }
  }
  return null;
}

function lcTransfertLink(id: number): PvEcartLinkedDocRow['link'] {
  return {
    path: '/transfert-entrepot/action/validation',
    queryParams: { id: String(id), readonly: '1' },
  };
}

function enrichLinkedViewWithLcTransfertLink(
  view: PvEcartLinkedDocumentsView,
  lcId: number,
  lcNumDocument?: string | null
): PvEcartLinkedDocumentsView {
  const lcRef = String(lcNumDocument ?? '').trim();
  const code =
    lcRef && !isPvEcartPlaceholderDocumentCode(lcRef)
      ? lcRef
      : view.transfert.code;
  return {
    ...view,
    transfert: {
      ...view.transfert,
      code,
      link: lcTransfertLink(lcId),
    },
  };
}

/** Numéro affiché pour le transfert entrepôt LC (priorité hidden / API LC). */
function resolveLcTransfertNumForLinkedDisplay(
  doc: Record<string, unknown>,
  lcNumFromApi: string | null | undefined
): string {
  const hidden = readMergedHidden(doc);
  const stockNum = String(hidden['stock_transfert_num_document'] ?? '').trim();
  if (stockNum) {
    return stockNum;
  }
  const fromDoc = readPvEcartLcTransfertNumFromDoc(doc);
  if (fromDoc) {
    return fromDoc;
  }
  const api = String(lcNumFromApi ?? '').trim();
  return api;
}

function applyPvEcartLinkedTransfertDisplayCode(
  view: PvEcartLinkedDocumentsView,
  doc: Record<string, unknown>,
  lcNumFromApi: string | null | undefined
): PvEcartLinkedDocumentsView {
  const lcRef = resolveLcTransfertNumForLinkedDisplay(doc, lcNumFromApi);
  const linkPath = String(view.transfert.link?.path ?? '');
  const isLcTransfert = linkPath.includes('transfert-entrepot');
  if (isLcTransfert && lcRef) {
    return {
      ...view,
      transfert: {
        ...view.transfert,
        code: lcRef,
      },
    };
  }
  return view;
}

/** Charge num_document LC via id hidden, hidden num ou workflow ERP. */
function resolveLcTransfertNumDocument$(
  doc: Record<string, unknown>,
  lcStockTransfertService: LogicielCaisseStockTransfertService | undefined,
  documentService: DocumentService
): Observable<string | null> {
  const localFallback = resolveLcTransfertNumForLinkedDisplay(doc, null);
  if (!lcStockTransfertService) {
    return of(localFallback || null);
  }
  const syncId = readPvEcartLcTransfertIdFromDoc(doc);
  const fromId$ =
    syncId != null && syncId > 0
      ? lcStockTransfertService.getById(syncId).pipe(
          map((detail) => String(detail.num_document ?? '').trim() || null),
          catchError(() => of(null))
        )
      : of<string | null>(null);

  return fromId$.pipe(
    switchMap((fromId) => {
      if (fromId) {
        return of(fromId);
      }
      if (localFallback) {
        return of(localFallback);
      }
      return resolvePvEcartLcReservationReference$(documentService, doc).pipe(
        catchError(() => of(null))
      );
    })
  );
}

/** Résout l’id LC via num_document (même logique que entrepôts / réservation PV). */
function resolveLcTransfertIdByNumDocument$(
  lcStockTransfertService: LogicielCaisseStockTransfertService,
  numDocument: string
): Observable<number | null> {
  const num = String(numDocument ?? '').trim();
  if (!num) {
    return of(null);
  }
  return lcStockTransfertService.lookupPhasesByNumDocument(num).pipe(
    map((phases) => {
      const id = pickLcTransfertPrimaryPhaseId(phases);
      return id != null && id > 0 ? id : null;
    }),
    catchError(() => of(null))
  );
}

export function buildPvEcartLinkedDocumentsView(
  pvDoc: unknown,
  options?: {
    rootDoc?: unknown;
    children?: any[];
    lcTransfertNumDocument?: string | null;
  }
): PvEcartLinkedDocumentsView {
  const doc = (pvDoc && typeof pvDoc === 'object' ? pvDoc : {}) as Record<string, unknown>;
  const hidden = readMergedHidden(doc);
  const children = options?.children ?? [];
  const bonTransfert = resolveBonTransfertFromWorkflow(options?.rootDoc, children);
  const bonTransfertErpId = Number(bonTransfert?.['id']);

  let lcId =
    extractLcTransfertIdFromPvDoc(doc) ??
    (options?.rootDoc || children.length
      ? readLcTransfertIdFromWorkflowDocs(doc, options?.rootDoc, children)
      : null);

  const lcIdFromExplicitPvKey = readPvEcartLcTransfertIdFromDoc(doc) != null;
  if (
    !lcIdFromExplicitPvKey &&
    lcId != null &&
    Number.isFinite(bonTransfertErpId) &&
    bonTransfertErpId > 0 &&
    lcId === bonTransfertErpId
  ) {
    lcId = null;
  }

  const stockNum = String(hidden['stock_transfert_num_document'] ?? '').trim();
  const lcNum = String(options?.lcTransfertNumDocument ?? '').trim();
  const bonSortie = pickLatestChildByType(children, STOCK_TRANSFER_WORKFLOW_TYPE_IDS.BON_SORTIE);
  const bonReception = pickLatestChildByType(children, STOCK_TRANSFER_WORKFLOW_TYPE_IDS.BON_RECEPTION);

  let transfertCode = stockNum || lcNum || '';
  // Transfert LC : ne pas afficher la ref ERP du bon de transfert (souvent incorrecte vs num_document LC).
  if (!transfertCode && lcId == null && bonTransfert) {
    transfertCode = formatErpDocumentCode(bonTransfert, { allowIdFallback: false });
    if (transfertCode === '—') {
      transfertCode = '';
    }
  }
  if (!transfertCode && lcId == null && bonTransfert) {
    transfertCode = formatErpDocumentCode(bonTransfert);
    if (transfertCode === '—') {
      transfertCode = '';
    }
  }

  const transfert: PvEcartLinkedDocRow = {
    label: 'Document de transfert',
    code: transfertCode || '—',
  };
  if (lcId != null) {
    transfert.link = lcTransfertLink(lcId);
  } else if (bonTransfert && Number(bonTransfert['id']) > 0) {
    transfert.link = {
      path: '/stock/bon-transfert/action/validation',
      queryParams: { id: String(bonTransfert['id']) },
    };
  }

  const bonSortieRow: PvEcartLinkedDocRow = {
    label: 'Bon de sortie',
    code: bonSortie ? formatErpDocumentCode(bonSortie, { allowIdFallback: false }) : '—',
  };
  if (bonSortie?.id) {
    bonSortieRow.link = {
      path: '/vente/bon-sortie/action/validation',
      queryParams: { id: String(bonSortie.id) },
    };
  }

  const bonReceptionRow: PvEcartLinkedDocRow = {
    label: 'Document de réception',
    code: bonReception ? formatErpDocumentCode(bonReception, { allowIdFallback: false }) : '—',
  };
  if (bonReception?.id) {
    bonReceptionRow.link = {
      path: '/achat/bon-reception/action/validation',
      queryParams: { id: String(bonReception.id), readonly: '1' },
    };
  }

  return {
    transfert,
    bonSortie: bonSortieRow,
    bonReception: bonReceptionRow,
  };
}

function docNeedsWorkflowRootAscent(typeId: number): boolean {
  return (
    typeId === STOCK_TRANSFER_WORKFLOW_TYPE_IDS.PV_ECART ||
    typeId === STOCK_TRANSFER_WORKFLOW_TYPE_IDS.BON_RECEPTION ||
    typeId === STOCK_TRANSFER_WORKFLOW_TYPE_IDS.BON_SORTIE
  );
}

/** Enveloppe API documents ERP (GET by id / original) → objet document plain. */
function unwrapDocumentApiPayload(res: unknown): Record<string, unknown> | null {
  if (res == null || typeof res !== 'object') {
    return null;
  }
  const r = res as Record<string, unknown>;
  const data = r['data'];
  if (data != null && typeof data === 'object' && !Array.isArray(data)) {
    const d = data as Record<string, unknown>;
    const doc = d['document'];
    if (doc != null && typeof doc === 'object' && !Array.isArray(doc)) {
      return doc as Record<string, unknown>;
    }
    return d;
  }
  return r;
}

function getDocumentByIdForLinkedLoad$(
  documentService: DocumentService,
  documentId: number,
  debugBrId?: number
): Observable<unknown> {
  const svc = documentService as DocumentService & {
    getDocumentByIdForBonReceptionLinkedDebug?: (id: number, brId?: number) => Observable<unknown>;
  };
  if (debugBrId != null && typeof svc.getDocumentByIdForBonReceptionLinkedDebug === 'function') {
    return svc.getDocumentByIdForBonReceptionLinkedDebug(documentId, debugBrId);
  }
  return documentService.getDocumentById(documentId);
}

function getDocumentsByOriginalIdForLinkedLoad$(
  documentService: DocumentService,
  originalId: number,
  debugBrId?: number
): Observable<unknown> {
  const svc = documentService as DocumentService & {
    getDocumentsByOriginalIdForBonReceptionLinkedDebug?: (
      originalId: number,
      brId?: number
    ) => Observable<unknown>;
  };
  if (debugBrId != null && typeof svc.getDocumentsByOriginalIdForBonReceptionLinkedDebug === 'function') {
    return svc.getDocumentsByOriginalIdForBonReceptionLinkedDebug(originalId, debugBrId);
  }
  return documentService.getDocumentsByOriginalId(originalId);
}

/** Remonte la chaîne document_original_id jusqu’à la racine BT (aligné backend ERP). */
function walkStockTransferWorkflowRoot$(
  documentService: DocumentService,
  currentId: number,
  hop = 0,
  debugBrId?: number
): Observable<number> {
  if (hop >= 8) {
    return of(currentId);
  }
  return getDocumentByIdForLinkedLoad$(documentService, currentId, debugBrId).pipe(
    switchMap((res) => {
      const payload = unwrapDocumentApiPayload(res);
      if (!payload) {
        return of(currentId);
      }
      const typeId = Number(
        payload['type_document_id'] ?? (payload['typeDocument'] as { id?: number })?.id
      );
      if (typeId === STOCK_TRANSFER_WORKFLOW_TYPE_IDS.BON_TRANSFERT) {
        const id = Number(payload['id']);
        const oid = Number(payload['document_original_id']);
        if (Number.isFinite(oid) && oid > 0 && oid !== id) {
          return of(oid);
        }
        return of(Number.isFinite(id) && id > 0 ? id : currentId);
      }
      if (docNeedsWorkflowRootAscent(typeId)) {
        const up = Number(payload['document_original_id']);
        if (Number.isFinite(up) && up > 0 && up !== currentId) {
          return walkStockTransferWorkflowRoot$(documentService, up, hop + 1, debugBrId);
        }
      }
      return of(currentId);
    }),
    catchError(() => of(currentId))
  );
}

/**
 * Racine workflow (bon de transfert) pour charger les documents liés (PV, BR, BS).
 */
function resolveWorkflowRootIdForPvEcartLinkedLoad$(
  documentService: DocumentService,
  doc: Record<string, unknown>,
  debugBrId?: number
): Observable<number | null> {
  const typeId = Number(
    doc['type_document_id'] ?? (doc['typeDocument'] as { id?: number })?.id
  );
  const firstRoot = resolveTransferWorkflowRootId(doc);
  if (firstRoot == null || firstRoot <= 0) {
    return of(null);
  }
  if (!docNeedsWorkflowRootAscent(typeId)) {
    return of(firstRoot);
  }
  return walkStockTransferWorkflowRoot$(documentService, firstRoot, 0, debugBrId);
}

function pvDocNeedsFullLoadForLinkedDocuments(doc: Record<string, unknown>): boolean {
  const hidden = readMergedHidden(doc);
  const hasLcHint =
    readPvEcartLcTransfertIdFromDoc(doc) != null ||
    String(hidden['stock_transfert_num_document'] ?? '').trim() !== '' ||
    String(hidden['logiciel_caisse_transfert_id'] ?? '').trim() !== '';
  if (hasLcHint) {
    return false;
  }
  const oid = doc['document_original_id'];
  return oid != null && oid !== '' && Number(oid) > 0;
}

function loadPvEcartDocumentForLinkedDocuments$(
  documentService: DocumentService,
  pvDoc: unknown
): Observable<Record<string, unknown>> {
  const doc = (pvDoc && typeof pvDoc === 'object' ? pvDoc : {}) as Record<string, unknown>;
  const pvId = Number(doc['id']);
  if (!pvDocNeedsFullLoadForLinkedDocuments(doc) || !Number.isFinite(pvId) || pvId <= 0) {
    return of(doc);
  }
  return documentService.getDocumentById(pvId).pipe(
    map((res) => {
      const full = (res?.data?.document ?? res?.data ?? res) as Record<string, unknown>;
      return full && typeof full === 'object' ? full : doc;
    }),
    catchError(() => of(doc))
  );
}

/** Charge les codes ERP workflow + numéro transfert LC pour un PV d'écart. */
export function loadPvEcartLinkedDocuments$(
  documentService: DocumentService,
  pvDoc: unknown,
  lcStockTransfertService?: LogicielCaisseStockTransfertService
): Observable<PvEcartLinkedDocumentsView> {
  return loadPvEcartDocumentForLinkedDocuments$(documentService, pvDoc).pipe(
    switchMap((doc) => loadPvEcartLinkedDocumentsFromDoc$(documentService, doc, lcStockTransfertService))
  );
}

/** Charge documents liés workflow pour un bon de réception (liste achat). */
export function loadBonReceptionLinkedDocuments$(
  documentService: DocumentService,
  brDoc: unknown,
  lcStockTransfertService?: LogicielCaisseStockTransfertService,
  debugBrId?: number
): Observable<PvEcartLinkedDocumentsView> {
  const raw = (brDoc && typeof brDoc === 'object' ? brDoc : {}) as Record<string, unknown>;
  const full = (raw['fullData'] as Record<string, unknown> | undefined) ?? raw;
  const merged: Record<string, unknown> = { ...full, ...raw };
  if (raw['fullData'] && typeof raw['fullData'] === 'object') {
    Object.assign(merged, raw['fullData'] as Record<string, unknown>);
    merged['id'] = (raw['fullData'] as Record<string, unknown>)['id'] ?? raw['id'];
  }
  const brId = Number(merged['id']);
  if (!Number.isFinite(brId) || brId <= 0) {
    return of({
      transfert: EMPTY_ROW('Document de transfert'),
      bonSortie: EMPTY_ROW('Bon de sortie'),
      bonReception: EMPTY_ROW('Document de réception'),
    });
  }
  const loadDetail$ =
    debugBrId != null &&
    typeof (documentService as DocumentService & {
      getDocumentByIdForBonReceptionLinkedDebug?: (id: number, brId?: number) => Observable<unknown>;
    }).getDocumentByIdForBonReceptionLinkedDebug === 'function'
      ? (
          documentService as DocumentService & {
            getDocumentByIdForBonReceptionLinkedDebug: (id: number, brId?: number) => Observable<unknown>;
          }
        ).getDocumentByIdForBonReceptionLinkedDebug(brId, debugBrId)
      : documentService.getDocumentById(brId);

  return loadDetail$.pipe(
    map((res) => {
      const payload = unwrapDocumentApiPayload(res);
      return payload ?? merged;
    }),
    catchError(() => of(merged)),
    switchMap((doc) =>
      loadPvEcartLinkedDocumentsFromDoc$(
        documentService,
        doc,
        lcStockTransfertService,
        debugBrId
      )
    )
  );
}

function loadPvEcartLinkedDocumentsFromDoc$(
  documentService: DocumentService,
  doc: Record<string, unknown>,
  lcStockTransfertService?: LogicielCaisseStockTransfertService,
  debugBrId?: number
): Observable<PvEcartLinkedDocumentsView> {
  return resolveWorkflowRootIdForPvEcartLinkedLoad$(documentService, doc, debugBrId).pipe(
    switchMap((rootId) => {
      return loadPvEcartLinkedDocumentsWithRootId$(
        documentService,
        doc,
        rootId,
        lcStockTransfertService,
        debugBrId
      );
    })
  );
}

function loadPvEcartLinkedDocumentsWithRootId$(
  documentService: DocumentService,
  doc: Record<string, unknown>,
  rootId: number | null,
  lcStockTransfertService?: LogicielCaisseStockTransfertService,
  debugBrId?: number
): Observable<PvEcartLinkedDocumentsView> {

  const lcNum$ = resolveLcTransfertNumDocument$(
    doc,
    lcStockTransfertService,
    documentService
  );

  const buildAndEnrich$ = (
    view: PvEcartLinkedDocumentsView,
    numHint: string | null
  ): Observable<PvEcartLinkedDocumentsView> => {
    let next = applyPvEcartLinkedTransfertDisplayCode(view, doc, numHint);
    const resolvedLcId = resolvePvEcartLinkedLcTransfertId(doc, next);
    if (resolvedLcId != null && !next.transfert.link?.path?.includes('transfert-entrepot')) {
      next = enrichLinkedViewWithLcTransfertLink(next, resolvedLcId, numHint);
      next = applyPvEcartLinkedTransfertDisplayCode(next, doc, numHint);
    }
    if (resolvePvEcartLinkedLcTransfertId(doc, next) != null || !lcStockTransfertService) {
      return of(next);
    }
    const num =
      numHint ||
      resolveLcTransfertNumForLinkedDisplay(doc, numHint) ||
      null;
    if (!num) {
      return of(next);
    }
    return resolveLcTransfertIdByNumDocument$(lcStockTransfertService, num).pipe(
      map((id) => {
        if (id == null) {
          return next;
        }
        const enriched = enrichLinkedViewWithLcTransfertLink(next, id, num);
        return applyPvEcartLinkedTransfertDisplayCode(enriched, doc, num);
      })
    );
  };

  if (rootId == null || !Number.isFinite(rootId) || rootId <= 0) {
    return lcNum$.pipe(
      switchMap((lcNum) => {
        const view = buildPvEcartLinkedDocumentsView(doc, { lcTransfertNumDocument: lcNum });
        return buildAndEnrich$(view, lcNum);
      })
    );
  }

  return lcNum$.pipe(
    switchMap((lcNum) =>
      forkJoin({
        childrenRes: getDocumentsByOriginalIdForLinkedLoad$(
          documentService,
          rootId,
          debugBrId
        ).pipe(catchError(() => of(null))),
        rootRes: getDocumentByIdForLinkedLoad$(documentService, rootId, debugBrId).pipe(
          catchError(() => of(null))
        ),
      }).pipe(
        switchMap(({ childrenRes, rootRes }) => {
          const children = childrenRes ? parseConvertedChildrenFromApiResponse(childrenRes) : [];
          const rootPayload = unwrapDocumentApiPayload(rootRes) ?? rootRes;
          return enrichWorkflowDocsForLinkedCodes$(documentService, children, rootPayload).pipe(
            switchMap(({ children: enrichedChildren, rootDoc: enrichedRoot }) => {
              const view = buildPvEcartLinkedDocumentsView(doc, {
                rootDoc: enrichedRoot,
                children: enrichedChildren,
                lcTransfertNumDocument: lcNum,
              });
              return buildAndEnrich$(view, lcNum);
            })
          );
        })
      )
    )
  );
}

export type PvEcartLinkedSourcesOpenLink = {
  kind?: string;
  path: string;
  queryParams?: Record<string, string>;
};

export type PvEcartLinkedSourcesDocumentOriginal = {
  label?: string;
  code?: string;
  source?: string;
  lc_transfert_id?: number | null;
  erp_document_id?: number | null;
  link?: PvEcartLinkedSourcesOpenLink | null;
};

export type PvEcartLinkedSourcesErpDoc = {
  erp_document_id?: number;
  type_label?: string;
  code?: string | null;
  link?: PvEcartLinkedSourcesOpenLink | null;
};

export type PvEcartLinkedSourcesItem = {
  pv_document_id?: number;
  error?: string;
  document_original?: PvEcartLinkedSourcesDocumentOriginal | null;
  related?: {
    lc_transfert?: {
      lc_transfert_id?: number | null;
      num_document?: string | null;
    } | null;
    erp_bon_sortie?: PvEcartLinkedSourcesErpDoc | null;
    erp_bon_transfert?: PvEcartLinkedSourcesErpDoc | null;
    erp_bon_reception?: PvEcartLinkedSourcesErpDoc | null;
  };
};

/**
 * Id document ERP lié (bon de transfert) pour l’entrepôt destination du PV d’écart.
 * Priorité : API linked-sources → racine workflow (document_original_id).
 */
export function resolvePvEcartLinkedErpDocumentIdForEntrepots(
  pvDoc: Record<string, unknown> | null | undefined,
  entry?: PvEcartLinkedSourcesItem | null
): number | null {
  const fromRelatedBt = Number(entry?.related?.erp_bon_transfert?.erp_document_id);
  if (Number.isFinite(fromRelatedBt) && fromRelatedBt > 0) {
    return fromRelatedBt;
  }
  const orig = entry?.document_original;
  const fromOrigErp = Number(orig?.erp_document_id);
  if (Number.isFinite(fromOrigErp) && fromOrigErp > 0) {
    return fromOrigErp;
  }
  const linkPath = String(orig?.link?.path ?? '');
  const linkIdRaw = orig?.link?.queryParams?.['id'];
  if (linkIdRaw != null && linkPath && !linkPath.includes('transfert-entrepot')) {
    const linkId = Number(linkIdRaw);
    if (Number.isFinite(linkId) && linkId > 0) {
      return linkId;
    }
  }
  const pvId = Number(pvDoc?.['id']);
  const root = resolvePvEcartBonTransfertWorkflowRootId(
    pvDoc ?? null,
    Number.isFinite(pvId) && pvId > 0 ? pvId : 0
  );
  if (Number.isFinite(root) && root > 0 && root !== pvId) {
    return root;
  }
  const oid = Number(pvDoc?.['document_original_id']);
  if (Number.isFinite(oid) && oid > 0 && oid !== pvId) {
    return oid;
  }
  return null;
}

function erpSummaryToRow(
  label: string,
  summary: PvEcartLinkedSourcesErpDoc | null | undefined
): PvEcartLinkedDocRow {
  const code = String(summary?.code ?? '').trim() || '—';
  const row: PvEcartLinkedDocRow = { label, code };
  if (summary?.link?.path) {
    row.link = {
      path: summary.link.path,
      queryParams: summary.link.queryParams,
    };
  }
  return row;
}

/** Réponse POST /api/documents/pv-ecart/linked-sources → ligne liste « Document original ». */
export function mapPvEcartLinkedSourcesToDocumentOriginalRow(
  item: PvEcartLinkedSourcesItem | null | undefined
): PvEcartLinkedDocRow | null {
  const d = item?.document_original;
  const lcNum = String(item?.related?.lc_transfert?.num_document ?? '').trim();
  const code = normalizeLcStockTransfertDisplayCode(d?.code, lcNum);
  if (!code || code === '—' || isPvEcartPlaceholderDocumentCode(code)) {
    return null;
  }
  const row: PvEcartLinkedDocRow = {
    label: String(d?.label ?? 'Document original'),
    code,
  };
  if (d?.link?.path) {
    row.link = {
      path: d.link.path,
      queryParams: d.link.queryParams,
    };
  }
  return row;
}

/** Panneau « Documents liés » depuis la réponse API unifiée ERP + LC. */
export function buildPvEcartLinkedDocumentsViewFromSourcesItem(
  item: PvEcartLinkedSourcesItem | null | undefined
): PvEcartLinkedDocumentsView {
  const lc = item?.related?.lc_transfert;
  const lcId = Number(lc?.lc_transfert_id);
  const lcNum = String(lc?.num_document ?? '').trim();
  const transfert: PvEcartLinkedDocRow = {
    label: 'Document de transfert',
    code: normalizeLcStockTransfertDisplayCode(item?.document_original?.code, lcNum),
  };
  if (Number.isFinite(lcId) && lcId > 0) {
    transfert.link = lcTransfertLink(lcId);
  } else if (item?.document_original?.source === 'lc_stock_transfert' && item.document_original.link) {
    transfert.code = normalizeLcStockTransfertDisplayCode(
      item.document_original.code,
      lcNum
    );
    transfert.link = {
      path: item.document_original.link.path,
      queryParams: item.document_original.link.queryParams,
    };
  } else if (item?.document_original?.source === 'erp_bon_transfert' && item.document_original.link) {
    transfert.code = String(item.document_original.code ?? transfert.code);
    transfert.link = {
      path: item.document_original.link.path,
      queryParams: item.document_original.link.queryParams,
    };
  } else {
    const bt = item?.related?.erp_bon_transfert;
    const btCode = String(bt?.code ?? '').trim();
    if (btCode && btCode !== '—') {
      transfert.code = btCode;
      if (bt?.link?.path) {
        transfert.link = {
          path: bt.link.path,
          queryParams: bt.link.queryParams,
        };
      }
    }
  }
  return {
    transfert,
    bonSortie: erpSummaryToRow('Bon de sortie', item?.related?.erp_bon_sortie),
    bonReception: erpSummaryToRow('Document de réception', item?.related?.erp_bon_reception),
  };
}

function linkedRowHasDisplayCode(row: PvEcartLinkedDocRow | null | undefined): boolean {
  const code = String(row?.code ?? '').trim();
  return !!code && code !== '—' && !isPvEcartPlaceholderDocumentCode(code);
}

/** Ligne transfert stock LC sur la liste bon de sortie (/vente/bon-sortie/tous). */
export function resolveBonSortieStockTransfertListRow(
  view: PvEcartLinkedDocumentsView,
  numDocumentHint?: string | null
): PvEcartLinkedDocRow | null {
  const code = normalizeLcStockTransfertDisplayCode(view.transfert?.code, numDocumentHint);
  if (code && code !== '—' && !isPvEcartPlaceholderDocumentCode(code)) {
    return {
      ...view.transfert,
      code,
    };
  }
  return null;
}

export function bonSortieStockTransfertViewHasDisplay(view: PvEcartLinkedDocumentsView | null | undefined): boolean {
  return resolveBonSortieStockTransfertListRow(view ?? {
    transfert: EMPTY_ROW('Document de transfert'),
    bonSortie: EMPTY_ROW('Bon de sortie'),
    bonReception: EMPTY_ROW('Document de réception'),
  }) != null;
}

/** Ligne « Document original » sur la liste bon de réception (LC / BT, pas le BS ni le BR courant). */
export function resolveBonReceptionDocumentOriginalListRow(
  view: PvEcartLinkedDocumentsView,
  currentBrId?: number
): PvEcartLinkedDocRow | null {
  if (linkedRowHasDisplayCode(view.transfert)) {
    return view.transfert;
  }
  const brId = Number(currentBrId);
  const recLinkId = Number(view.bonReception?.link?.queryParams?.['id']);
  if (
    linkedRowHasDisplayCode(view.bonReception) &&
    !(Number.isFinite(brId) && brId > 0 && recLinkId === brId)
  ) {
    return view.bonReception;
  }
  if (view.transfert.link) {
    return view.transfert;
  }
  return null;
}

export function bonReceptionLinkedViewHasBonSortie(view: PvEcartLinkedDocumentsView | null | undefined): boolean {
  return linkedRowHasDisplayCode(view?.bonSortie) || !!view?.bonSortie?.link;
}

/** Bon de sortie — deuxième ligne liste PV d’écart (shared). */
export function resolvePvEcartBonSortieListRow(
  view: PvEcartLinkedDocumentsView | null | undefined
): PvEcartLinkedDocRow | null {
  const bs = view?.bonSortie;
  if (!bs) {
    return null;
  }
  const code = String(bs.code ?? '').trim();
  if (!code || code === '—') {
    return bs.link ? bs : null;
  }
  return bs;
}

/** Document original affiché (priorité API linked-sources, repli vue workflow). */
export function pvEcartLinkedViewHasDisplayableCode(
  view: PvEcartLinkedDocumentsView | null | undefined
): boolean {
  if (!view) {
    return false;
  }
  return (
    linkedRowHasDisplayCode(view.transfert) ||
    linkedRowHasDisplayCode(view.bonSortie) ||
    linkedRowHasDisplayCode(view.bonReception)
  );
}

function pickRicherLinkedRow(
  primary: PvEcartLinkedDocRow,
  secondary: PvEcartLinkedDocRow
): PvEcartLinkedDocRow {
  if (linkedRowHasDisplayCode(primary)) {
    return primary;
  }
  if (linkedRowHasDisplayCode(secondary)) {
    return secondary;
  }
  return primary.link ? primary : secondary;
}

/** Fusionne API linked-sources et résolution client (approve / document PV). */
export function mergePvEcartLinkedDocumentsFromSourcesAndClient(
  entry: PvEcartLinkedSourcesItem | null | undefined,
  clientView: PvEcartLinkedDocumentsView | null | undefined,
  doc?: Record<string, unknown> | null
): { view: PvEcartLinkedDocumentsView | null; documentOriginal: PvEcartLinkedDocRow | null } {
  const sourcesOk = entry && !entry.error;
  const sourcesView = sourcesOk ? buildPvEcartLinkedDocumentsViewFromSourcesItem(entry) : null;
  const sourcesOriginal = sourcesOk ? mapPvEcartLinkedSourcesToDocumentOriginalRow(entry) : null;

  let view: PvEcartLinkedDocumentsView | null = null;
  if (sourcesView && clientView) {
    view = {
      transfert: pickRicherLinkedRow(sourcesView.transfert, clientView.transfert),
      bonSortie: pickRicherLinkedRow(sourcesView.bonSortie, clientView.bonSortie),
      bonReception: pickRicherLinkedRow(sourcesView.bonReception, clientView.bonReception),
    };
  } else if (pvEcartLinkedViewHasDisplayableCode(sourcesView)) {
    view = sourcesView;
  } else if (pvEcartLinkedViewHasDisplayableCode(clientView)) {
    view = clientView ?? null;
  } else {
    view = sourcesView ?? clientView ?? null;
  }

  let documentOriginal =
    sourcesOriginal ??
    (view ? resolvePvEcartDocumentOriginalListRow(view) : null);

  const headerRef = doc ? resolvePvEcartLinkedTransfertHeaderReference(doc, view) : '';
  if (
    doc &&
    view &&
    !resolvePvEcartDocumentOriginalDisplayRow(documentOriginal, view) &&
    headerRef
  ) {
    const lcId = resolvePvEcartLinkedLcTransfertId(doc, view);
    const transfert: PvEcartLinkedDocRow = {
      label: 'Document de transfert',
      code: headerRef,
      ...(lcId != null && lcId > 0 ? { link: lcTransfertLink(lcId) } : {}),
    };
    view = { ...view, transfert };
    documentOriginal = transfert;
  }

  return { view, documentOriginal };
}

export function resolvePvEcartDocumentOriginalDisplayRow(
  originalFromSources: PvEcartLinkedDocRow | null | undefined,
  view: PvEcartLinkedDocumentsView | null | undefined
): PvEcartLinkedDocRow | null {
  if (originalFromSources) {
    const code = String(originalFromSources.code ?? '').trim();
    if (code && code !== '—') {
      return originalFromSources;
    }
  }
  if (!view) {
    return null;
  }
  return resolvePvEcartDocumentOriginalListRow(view);
}

/** Ligne unique « Document original » sur la liste PV d’écart (pas le bon de sortie). */
export function resolvePvEcartDocumentOriginalListRow(
  view: PvEcartLinkedDocumentsView
): PvEcartLinkedDocRow | null {
  const hasCode = (row: PvEcartLinkedDocRow): boolean => {
    const code = String(row.code ?? '').trim();
    return !!code && code !== '—' && !isPvEcartPlaceholderDocumentCode(code);
  };
  if (hasCode(view.bonReception)) {
    return view.bonReception;
  }
  if (hasCode(view.transfert)) {
    return view.transfert;
  }
  if (view.bonReception.link) {
    return view.bonReception;
  }
  if (view.transfert.link) {
    return view.transfert;
  }
  return null;
}

/** Complète ref_externe des documents workflow quand la liste ne renvoie que l’id. */
function enrichWorkflowDocsForLinkedCodes$(
  documentService: DocumentService,
  children: any[],
  rootDoc: unknown
): Observable<{ children: any[]; rootDoc: unknown }> {
  const ids = new Set<number>();
  const rootId = Number((rootDoc as Record<string, unknown> | null)?.['id']);
  if (Number.isFinite(rootId) && rootId > 0) {
    ids.add(rootId);
  }
  for (const c of children || []) {
    const id = Number(c?.id);
    if (Number.isFinite(id) && id > 0) {
      ids.add(id);
    }
  }
  const missing = [...ids].filter((id) => {
    const fromList =
      id === rootId
        ? (rootDoc as Record<string, unknown> | null)
        : (children || []).find((c) => Number(c?.id) === id);
    const code = formatErpDocumentCode(fromList, { allowIdFallback: false });
    return isPvEcartPlaceholderDocumentCode(code);
  });
  if (!missing.length) {
    return of({ children, rootDoc });
  }
  return forkJoin(
    missing.map((id) =>
      documentService.getDocumentById(id).pipe(
        map((res) => {
          const payload = (res?.data?.document ?? res?.data ?? res) as Record<string, unknown>;
          return { id, doc: payload };
        }),
        catchError(() => of({ id, doc: null as Record<string, unknown> | null }))
      )
    )
  ).pipe(
    map((fetched) => {
      const byId = new Map<number, Record<string, unknown>>();
      for (const f of fetched) {
        if (f.doc && typeof f.doc === 'object') {
          byId.set(f.id, f.doc);
        }
      }
      const enrichedChildren = (children || []).map((c) => {
        const id = Number(c?.id);
        return byId.get(id) ?? c;
      });
      const enrichedRoot =
        rootId && byId.has(rootId) ? byId.get(rootId)! : (rootDoc as Record<string, unknown> | null);
      return { children: enrichedChildren, rootDoc: enrichedRoot ?? rootDoc };
    })
  );
}

export function emptyPvEcartLinkedDocumentsView(): PvEcartLinkedDocumentsView {
  return {
    transfert: EMPTY_ROW('Document de transfert'),
    bonSortie: EMPTY_ROW('Bon de sortie'),
    bonReception: EMPTY_ROW('Document de réception'),
  };
}

/** Lignes à afficher dans la liste PV (bon transfert + bon de sortie + réception si connus). */
export function listPvEcartLinkedRowsForDisplay(view: PvEcartLinkedDocumentsView): PvEcartLinkedDocRow[] {
  return [view.transfert, view.bonSortie, view.bonReception].filter((row) => {
    const code = String(row.code ?? '').trim();
    if (code && code !== '—') {
      return true;
    }
    return !!row.link;
  });
}

export function openPvEcartLinkedDocInNewTab(
  router: Router,
  link: { path: string; queryParams?: Record<string, string> }
): void {
  const tree = router.createUrlTree([link.path], { queryParams: link.queryParams });
  const url = router.serializeUrl(tree);
  window.open(url, '_blank', 'noopener');
}

/** Id document ERP bon de transfert depuis la ligne « Document de transfert » (pas le lien LC seul). */
export function readErpBonTransfertIdFromPvLinkedView(
  view: PvEcartLinkedDocumentsView | null | undefined
): number | null {
  const link = view?.transfert?.link;
  if (!link) {
    return null;
  }
  const path = String(link.path ?? '');
  if (!path.includes('bon-transfert/action')) {
    return null;
  }
  const n = Number(link.queryParams?.['id']);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Bon de transfert ERP lié au PV (workflow) — pour ouvrir approve-vente après correction stock LC. */
export function resolveErpBonTransfertIdForPvEcart$(
  documentService: DocumentService,
  pvDocOrId: number | Record<string, unknown>,
  lcStockTransfert?: LogicielCaisseStockTransfertService
): Observable<number | null> {
  const pvDoc$ =
    typeof pvDocOrId === 'number'
      ? documentService.getDocumentById(pvDocOrId).pipe(
          map(
            (res) =>
              (res?.data?.document ?? res?.data ?? res) as Record<string, unknown> | null
          ),
          catchError(() => of(null))
        )
      : of(pvDocOrId);

  return pvDoc$.pipe(
    switchMap((doc) => {
      if (!doc || typeof doc !== 'object') {
        return of(null);
      }
      return loadPvEcartLinkedDocuments$(documentService, doc, lcStockTransfert).pipe(
        switchMap((view) => {
          const fromLink = readErpBonTransfertIdFromPvLinkedView(view);
          if (fromLink != null) {
            return of(fromLink);
          }
          const rootId = resolveTransferWorkflowRootId(doc);
          if (rootId == null || !Number.isFinite(rootId) || rootId <= 0) {
            return of(null);
          }
          return documentService.getDocumentsByOriginalId(rootId).pipe(
            map((res) => {
              const children = parseConvertedChildrenFromApiResponse(res);
              const bt = resolveBonTransfertFromWorkflow(null, children);
              const n = Number(bt?.['id']);
              return Number.isFinite(n) && n > 0 ? n : null;
            }),
            catchError(() => of(null))
          );
        })
      );
    })
  );
}

/** Id transfert LC lié au PV (hidden, workflow ou lien « Documents liés »). */
export function resolvePvEcartLinkedLcTransfertId(
  doc: Record<string, unknown>,
  view?: PvEcartLinkedDocumentsView | null
): number | null {
  const erpId = Number(doc['id']);
  const fromHidden =
    readPvEcartLcTransfertIdFromDoc(doc) ??
    extractLogicielCaisseTransfertId(
      doc,
      Number.isFinite(erpId) && erpId > 0 ? erpId : null
    );
  if (fromHidden != null && fromHidden > 0) {
    return fromHidden;
  }
  const link = view?.transfert?.link;
  if (link?.path?.includes('transfert-entrepot') && link.queryParams) {
    for (const key of ['id', 'lcTransfertId'] as const) {
      const raw = link.queryParams[key];
      if (raw == null || String(raw).trim() === '') {
        continue;
      }
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) {
        return n;
      }
    }
  }
  return null;
}

/**
 * Id transfert LC pour navigation après « Valider » :
 * hidden PV → lien Documents liés → workflow BT → num_document LC (comme entrepôts PV).
 */
export function resolvePvEcartLinkedLcTransfertId$(
  documentService: DocumentService,
  lcStockTransfertService: LogicielCaisseStockTransfertService | undefined,
  doc: Record<string, unknown>,
  view?: PvEcartLinkedDocumentsView | null
): Observable<number | null> {
  const sync = resolvePvEcartLinkedLcTransfertId(doc, view);
  if (sync != null) {
    return of(sync);
  }

  const rootId = resolveTransferWorkflowRootId(doc);
  const workflowLc$ =
    rootId != null && rootId > 0
      ? documentService.getDocumentsByOriginalId(rootId).pipe(
          switchMap((childrenRes) => {
            const children = parseConvertedChildrenFromApiResponse(childrenRes);
            const fromChildren = readLcTransfertIdFromWorkflowDocs(doc, null, children);
            if (fromChildren != null) {
              return of(fromChildren);
            }
            return documentService.getDocumentById(rootId).pipe(
              map((rootRes) => {
                const rootPayload = rootRes?.data?.document ?? rootRes?.data ?? rootRes;
                return readLcTransfertIdFromWorkflowDocs(doc, rootPayload, children);
              }),
              catchError(() => of(null))
            );
          }),
          catchError(() => of(null))
        )
      : of(null);

  const lcByNum$ =
    lcStockTransfertService != null
      ? resolvePvEcartLcReservationReference$(documentService, doc).pipe(
          switchMap((num) =>
            num
              ? resolveLcTransfertIdByNumDocument$(lcStockTransfertService, num)
              : of(null)
          )
        )
      : of(null);

  return workflowLc$.pipe(
    switchMap((fromWorkflow) => {
      if (fromWorkflow != null) {
        return of(fromWorkflow);
      }
      return lcByNum$;
    })
  );
}

/** Num LC depuis hidden_client_fields / hidden_fournisseur_fields (liste ou fiche document). */
export function readStockTransfertNumFromDocument(doc: unknown): string | null {
  const raw = doc as Record<string, unknown> | null | undefined;
  const merged =
    (raw?.['fullData'] as Record<string, unknown> | undefined) ?? raw;
  if (!merged || typeof merged !== 'object') {
    return null;
  }
  const sources = [merged['hidden_client_fields'], merged['hidden_fournisseur_fields']];
  for (const src of sources) {
    let hidden: Record<string, unknown> | null = null;
    if (typeof src === 'string') {
      try {
        hidden = JSON.parse(src) as Record<string, unknown>;
      } catch {
        hidden = null;
      }
    } else if (src && typeof src === 'object') {
      hidden = src as Record<string, unknown>;
    }
    const num = String(hidden?.['stock_transfert_num_document'] ?? '').trim();
    if (num) {
      return num;
    }
  }
  return null;
}

function linkedViewFromStockTransfertErpRefsItem(
  item: StockTransfertErpLinkedRefsItem
): PvEcartLinkedDocumentsView {
  const lcId = Number(item.lc_transfert?.lc_transfert_id);
  const lcNum = String(
    item.lc_transfert?.num_document ?? item.stock_transfert_num_document ?? ''
  ).trim();
  const transfert: PvEcartLinkedDocRow = {
    label: 'Document de transfert',
    code: normalizeLcStockTransfertDisplayCode(null, lcNum),
  };
  if (Number.isFinite(lcId) && lcId > 0) {
    transfert.link = lcTransfertLink(lcId);
  } else {
    const bt = item.erp_bon_transfert;
    const btCode = erpLinkedRefToDisplayCode(bt);
    if (btCode) {
      transfert.code = btCode;
    }
    if (bt?.link?.path) {
      transfert.link = {
        path: bt.link.path,
        queryParams: bt.link.queryParams,
      };
    }
  }
  const bs = item.erp_bon_sortie;
  const bonSortie: PvEcartLinkedDocRow = {
    label: 'Bon de sortie',
    code: erpLinkedRefToDisplayCode(bs) || '—',
  };
  if (bs?.link?.path) {
    bonSortie.link = {
      path: bs.link.path,
      queryParams: bs.link.queryParams,
    };
  }
  return {
    transfert,
    bonSortie,
    bonReception: { label: 'Document de réception', code: '—' },
  };
}

function loadBonSortieViaStockTransfertErpRefs$(
  documentService: DocumentService,
  doc: unknown,
  numHint: string | null,
  stationId?: number | null
): Observable<PvEcartLinkedDocumentsView | null> {
  const num = String(numHint ?? readStockTransfertNumFromDocument(doc) ?? '').trim();
  if (!num) {
    return of(null);
  }
  const sid = stationId != null ? Number(stationId) : NaN;
  return documentService
    .postStockTransfertErpLinkedRefs({
      num_document: num,
      ...(Number.isFinite(sid) && sid > 0 ? { station_id: sid } : {}),
    })
    .pipe(
      map((items) => {
        const entry = lookupStockTransfertErpLinkedRefsEntry(items, num);
        if (!entry || entry.error) {
          return null;
        }
        return linkedViewFromStockTransfertErpRefsItem(entry);
      }),
      catchError(() => of(null))
    );
}

function resolveBonSortieLinkedTransfertDisplayRow(
  view: PvEcartLinkedDocumentsView | null | undefined,
  entry: PvEcartLinkedSourcesItem | null | undefined,
  numHint: string | null
): PvEcartLinkedDocRow | null {
  const lcNum = String(entry?.related?.lc_transfert?.num_document ?? '').trim();
  let row =
    mapPvEcartLinkedSourcesToDocumentOriginalRow(entry) ??
    (view ? resolveBonSortieStockTransfertListRow(view, lcNum || numHint) : null);
  if (!row) {
    return null;
  }
  const code = normalizeLcStockTransfertDisplayCode(row.code, lcNum || numHint);
  if (!code || code === '—' || isPvEcartPlaceholderDocumentCode(code)) {
    return row.link ? { ...row, code: lcNum || numHint || row.code } : null;
  }
  return { ...row, code };
}

export type BonSortieLinkedStockTransfertBundle = {
  transfertRow: PvEcartLinkedDocRow | null;
  entry?: PvEcartLinkedSourcesItem;
};

function resolveBonSortieLinkedStockTransfertBundle$(
  documentService: DocumentService,
  bsDoc: unknown,
  lcStockTransfertService?: LogicielCaisseStockTransfertService,
  options?: { stationId?: number | null }
): Observable<BonSortieLinkedStockTransfertBundle> {
  const raw = (bsDoc && typeof bsDoc === 'object' ? bsDoc : {}) as Record<string, unknown>;
  const full = (raw['fullData'] as Record<string, unknown> | undefined) ?? raw;
  const merged: Record<string, unknown> = { ...full, ...raw };
  if (raw['fullData'] && typeof raw['fullData'] === 'object') {
    Object.assign(merged, raw['fullData'] as Record<string, unknown>);
    merged['id'] = (raw['fullData'] as Record<string, unknown>)['id'] ?? raw['id'];
  }
  const bsId = Number(merged['id']);
  const numHint = readStockTransfertNumFromDocument(bsDoc);
  if (!Number.isFinite(bsId) || bsId <= 0) {
    return of({ transfertRow: null });
  }

  const clientFallback$ = loadBonReceptionLinkedDocuments$(
    documentService,
    bsDoc,
    lcStockTransfertService,
    bsId
  ).pipe(
    switchMap((view) => {
      const fromView = resolveBonSortieLinkedTransfertDisplayRow(view, undefined, numHint);
      if (fromView) {
        return of({ transfertRow: fromView, entry: undefined as PvEcartLinkedSourcesItem | undefined });
      }
      return loadBonSortieViaStockTransfertErpRefs$(
        documentService,
        bsDoc,
        numHint,
        options?.stationId
      ).pipe(
        map((refsView) => ({
          transfertRow: resolveBonSortieLinkedTransfertDisplayRow(
            refsView ?? view,
            undefined,
            numHint
          ),
          entry: undefined as PvEcartLinkedSourcesItem | undefined,
        }))
      );
    }),
    catchError(() => of({ transfertRow: null }))
  );

  return documentService.postBonSortieLinkedStockTransfert([bsId]).pipe(
    switchMap((items) => {
      const entry = items[String(bsId)] as PvEcartLinkedSourcesItem | undefined;
      const usable =
        entry &&
        !entry.error &&
        (!!mapPvEcartLinkedSourcesToDocumentOriginalRow(entry) ||
          bonSortieStockTransfertViewHasDisplay(
            buildPvEcartLinkedDocumentsViewFromSourcesItem(entry)
          ));
      if (usable) {
        const view = buildPvEcartLinkedDocumentsViewFromSourcesItem(entry);
        const row = resolveBonSortieLinkedTransfertDisplayRow(view, entry, numHint);
        if (row) {
          return of({ transfertRow: row, entry });
        }
      }
      return clientFallback$;
    }),
    catchError(() => clientFallback$)
  );
}

/** Transfert LC lié à un bon de sortie (validation / fiche), même logique que la liste shared. */
export function loadBonSortieLinkedStockTransfertRow$(
  documentService: DocumentService,
  bsDoc: unknown,
  lcStockTransfertService?: LogicielCaisseStockTransfertService,
  options?: { stationId?: number | null }
): Observable<PvEcartLinkedDocRow | null> {
  return resolveBonSortieLinkedStockTransfertBundle$(
    documentService,
    bsDoc,
    lcStockTransfertService,
    options
  ).pipe(map((bundle) => bundle.transfertRow));
}

/** Ligne tableau « Documents liés » popup bon de sortie (transfert LC, comme colonne shared). */
export function bonSortieLinkedToDocumentsLiesSkeletons(
  transfertRow: PvEcartLinkedDocRow | null | undefined,
  entry?: PvEcartLinkedSourcesItem,
  numHint?: string | null
): DocumentsLieSkeletonRow[] {
  if (!transfertRow) {
    return [];
  }
  const code = String(transfertRow.code ?? '').trim();
  if (!code || code === '—') {
    if (!transfertRow.link) {
      return [];
    }
  }
  const orig = entry?.document_original;
  const origErp = Number(orig?.erp_document_id);
  const lcId = Number(
    orig?.lc_transfert_id ?? entry?.related?.lc_transfert?.lc_transfert_id
  );
  const isLcOriginal =
    orig?.source === 'lc_stock_transfert' ||
    (!Number.isFinite(origErp) || origErp <= 0);
  const displayCode =
    code && code !== '—'
      ? normalizeLcStockTransfertDisplayCode(code, numHint)
      : normalizeLcStockTransfertDisplayCode(null, numHint) || '—';
  return [
    {
      type: String(transfertRow.label ?? orig?.label ?? 'Transfert entrepôt').trim() || 'Transfert entrepôt',
      reference: displayCode,
      date: '--',
      montant: 0,
      statut: '--',
      ...(transfertRow.link ? { _lcWorkflowLink: transfertRow.link } : {}),
      ...(Number.isFinite(origErp) && origErp > 0
        ? { erpDocumentId: origErp }
        : { erpDocumentId: erpIdFromWorkflowLink(transfertRow.link) }),
      ...(isLcOriginal && Number.isFinite(lcId) && lcId > 0 ? { lcTransfertId: lcId } : {}),
    },
  ];
}

/** Tableau popup bon de sortie : transfert LC + date / montant / statut. */
export function loadBonSortieDocumentsLiesTableRows$(
  documentService: DocumentService,
  bsDoc: unknown,
  lcStockTransfertService?: LogicielCaisseStockTransfertService,
  options?: { stationId?: number | null }
): Observable<DocumentsLieSkeletonRow[]> {
  const numHint = readStockTransfertNumFromDocument(bsDoc);
  return resolveBonSortieLinkedStockTransfertBundle$(
    documentService,
    bsDoc,
    lcStockTransfertService,
    options
  ).pipe(
    switchMap(({ transfertRow, entry }) => {
      const skeletons = bonSortieLinkedToDocumentsLiesSkeletons(
        transfertRow,
        entry,
        numHint
      );
      if (!skeletons.length) {
        return of([]);
      }
      return enrichDocumentsLieSkeletonRows$(
        documentService,
        skeletons,
        lcStockTransfertService ?? null
      );
    })
  );
}

function pvEcartLinkedDocRowToSkeleton(
  row: PvEcartLinkedDocRow,
  entry: PvEcartLinkedSourcesItem | undefined,
  role: 'original' | 'bonSortie'
): DocumentsLieSkeletonRow {
  const code = String(row.code ?? '').trim();
  const orig = entry?.document_original;
  const bs = entry?.related?.erp_bon_sortie;
  const lcId = Number(
    orig?.lc_transfert_id ?? entry?.related?.lc_transfert?.lc_transfert_id
  );
  if (role === 'bonSortie') {
    const bsErp = Number(bs?.erp_document_id);
    return {
      type: String(row.label ?? 'Bon de sortie').trim() || 'Bon de sortie',
      reference: code && code !== '—' ? code : '—',
      date: '--',
      montant: 0,
      statut: '--',
      ...(row.link ? { _lcWorkflowLink: row.link } : {}),
      erpDocumentId:
        Number.isFinite(bsErp) && bsErp > 0
          ? bsErp
          : erpIdFromWorkflowLink(row.link),
    };
  }
  const origErp = Number(orig?.erp_document_id);
  const isLcOriginal =
    orig?.source === 'lc_stock_transfert' ||
    (!Number.isFinite(origErp) || origErp <= 0);
  return {
    type: String(row.label ?? orig?.label ?? 'Document').trim() || 'Document',
    reference: code && code !== '—' ? code : '—',
    date: '--',
    montant: 0,
    statut: '--',
    ...(row.link ? { _lcWorkflowLink: row.link } : {}),
    ...(Number.isFinite(origErp) && origErp > 0
      ? { erpDocumentId: origErp }
      : { erpDocumentId: erpIdFromWorkflowLink(row.link) }),
    ...(isLcOriginal && Number.isFinite(lcId) && lcId > 0
      ? { lcTransfertId: lcId }
      : {}),
  };
}

/** Lignes tableau popup PV d’écart (transfert LC + bon de sortie, comme liste shared). */
export function pvEcartLinkedToDocumentsLiesSkeletons(
  documentOriginal: PvEcartLinkedDocRow | null,
  view: PvEcartLinkedDocumentsView | null,
  entry?: PvEcartLinkedSourcesItem
): DocumentsLieSkeletonRow[] {
  const displayRows: PvEcartLinkedDocRow[] = [];
  const pushUnique = (row: PvEcartLinkedDocRow | null | undefined): void => {
    if (!row) {
      return;
    }
    const code = String(row.code ?? '').trim();
    if (!code || code === '—') {
      if (row.link) {
        displayRows.push(row);
      }
      return;
    }
    if (displayRows.some((r) => String(r.code ?? '').trim() === code)) {
      return;
    }
    displayRows.push(row);
  };
  const origRow = resolvePvEcartDocumentOriginalDisplayRow(documentOriginal, view);
  const bsRow = view ? resolvePvEcartBonSortieListRow(view) : null;
  pushUnique(origRow);
  pushUnique(bsRow);

  return displayRows.map((row) => {
    const isBonSortie =
      !!bsRow &&
      String(bsRow.code ?? '').trim() === String(row.code ?? '').trim() &&
      (!origRow ||
        String(origRow.code ?? '').trim() !== String(row.code ?? '').trim());
    return pvEcartLinkedDocRowToSkeleton(
      row,
      entry,
      isBonSortie ? 'bonSortie' : 'original'
    );
  });
}

/** Tableau popup PV d’écart : documents liés workflow + date / montant / statut. */
export function loadPvEcartDocumentsLiesTableRows$(
  documentService: DocumentService,
  pvDoc: unknown,
  lcStockTransfertService?: LogicielCaisseStockTransfertService
): Observable<DocumentsLieSkeletonRow[]> {
  const raw = (pvDoc && typeof pvDoc === 'object' ? pvDoc : {}) as Record<string, unknown>;
  const full = (raw['fullData'] as Record<string, unknown> | undefined) ?? raw;
  const merged: Record<string, unknown> = { ...full, ...raw };
  if (raw['fullData'] && typeof raw['fullData'] === 'object') {
    Object.assign(merged, raw['fullData'] as Record<string, unknown>);
    merged['id'] = (raw['fullData'] as Record<string, unknown>)['id'] ?? raw['id'];
  }
  const pvId = Number(merged['id']);
  if (!Number.isFinite(pvId) || pvId <= 0) {
    return of([]);
  }
  const docRecord = merged;

  return forkJoin({
    sources: documentService
      .postPvEcartLinkedSources([pvId])
      .pipe(catchError(() => of({} as Record<string, PvEcartLinkedSourcesItem>))),
    view: loadPvEcartLinkedDocuments$(
      documentService,
      pvDoc,
      lcStockTransfertService
    ).pipe(catchError(() => of(null))),
  }).pipe(
    switchMap(({ sources, view }) => {
      const entry = sources[String(pvId)] as PvEcartLinkedSourcesItem | undefined;
      const mergedLinks = mergePvEcartLinkedDocumentsFromSourcesAndClient(
        entry,
        view,
        docRecord
      );
      const skeletons = pvEcartLinkedToDocumentsLiesSkeletons(
        mergedLinks.documentOriginal,
        mergedLinks.view,
        entry
      );
      if (!skeletons.length) {
        return of([]);
      }
      return enrichDocumentsLieSkeletonRows$(
        documentService,
        skeletons,
        lcStockTransfertService ?? null
      );
    })
  );
}

/** Affichage inline (virgule) à partir de la ligne transfert LC bon de sortie. */
export function bonSortieLinkedTransfertToLcInlineLinks(
  row: PvEcartLinkedDocRow | null | undefined
): LcReceptionErpWorkflowLinkRow[] {
  if (!row) {
    return [];
  }
  const code = String(row.code ?? '').trim();
  if (!code || code === '—') {
    return row.link ? [{ code: '—', typeLabel: 'Transfert entrepôt', link: row.link }] : [];
  }
  return [
    {
      code,
      typeLabel: row.label || 'Transfert entrepôt',
      link: row.link,
    },
  ];
}

export type BonReceptionLinkedWorkflowDisplay = {
  documentOriginal: PvEcartLinkedDocRow | null;
  bonSortie: PvEcartLinkedDocRow | null;
};

export type BonReceptionDocumentsLiesTableRow = DocumentsLieSkeletonRow;

function erpIdFromWorkflowLink(
  link: PvEcartLinkedDocRow['link'] | undefined
): number | null {
  const id = Number(link?.queryParams?.['id']);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/** Lignes onglet « Documents liés » (popup bon réception), alignées liste shared + métadonnées enrichissement. */
export function bonReceptionLinkedWorkflowToDocumentsLiesSkeletons(
  display: BonReceptionLinkedWorkflowDisplay,
  entry?: PvEcartLinkedSourcesItem
): DocumentsLieSkeletonRow[] {
  const rows: DocumentsLieSkeletonRow[] = [];
  const pushOriginal = (row: PvEcartLinkedDocRow | null | undefined) => {
    if (!row) {
      return;
    }
    const code = String(row.code ?? '').trim();
    if (!code || code === '—') {
      if (!row.link) {
        return;
      }
    }
    const orig = entry?.document_original;
    const origErp = Number(orig?.erp_document_id);
    const lcId = Number(
      orig?.lc_transfert_id ?? entry?.related?.lc_transfert?.lc_transfert_id
    );
    const isLcOriginal = orig?.source === 'lc_stock_transfert';
    rows.push({
      type: String(row.label ?? orig?.label ?? 'Document').trim() || 'Document',
      reference: code && code !== '—' ? code : '—',
      date: '--',
      montant: 0,
      statut: '--',
      ...(row.link ? { _lcWorkflowLink: row.link } : {}),
      ...(Number.isFinite(origErp) && origErp > 0
        ? { erpDocumentId: origErp }
        : { erpDocumentId: erpIdFromWorkflowLink(row.link) }),
      ...(isLcOriginal && Number.isFinite(lcId) && lcId > 0
        ? { lcTransfertId: lcId }
        : {}),
    });
  };
  const pushBonSortie = (row: PvEcartLinkedDocRow | null | undefined) => {
    if (!row) {
      return;
    }
    const code = String(row.code ?? '').trim();
    if (!code || code === '—') {
      if (!row.link) {
        return;
      }
    }
    const bsErp = Number(entry?.related?.erp_bon_sortie?.erp_document_id);
    rows.push({
      type: String(row.label ?? 'Bon de sortie').trim() || 'Bon de sortie',
      reference: code && code !== '—' ? code : '—',
      date: '--',
      montant: 0,
      statut: '--',
      ...(row.link ? { _lcWorkflowLink: row.link } : {}),
      erpDocumentId:
        Number.isFinite(bsErp) && bsErp > 0
          ? bsErp
          : erpIdFromWorkflowLink(row.link),
    });
  };
  pushOriginal(display.documentOriginal);
  pushBonSortie(display.bonSortie);
  return rows;
}

function bonReceptionLinkedSourcesEntryIsUsable(
  entry: PvEcartLinkedSourcesItem | undefined
): boolean {
  if (!entry || entry.error) {
    return false;
  }
  const view = buildPvEcartLinkedDocumentsViewFromSourcesItem(entry);
  return (
    !!mapPvEcartLinkedSourcesToDocumentOriginalRow(entry) ||
    bonReceptionLinkedViewHasBonSortie(view)
  );
}

function resolveBonReceptionBonSortieDisplayRow(
  view: PvEcartLinkedDocumentsView | null | undefined
): PvEcartLinkedDocRow | null {
  const bs = view?.bonSortie;
  if (!bs) {
    return null;
  }
  const code = String(bs.code ?? '').trim();
  if (!code || code === '—') {
    return bs.link ? bs : null;
  }
  return bs;
}

function toBonReceptionLinkedWorkflowDisplay(
  view: PvEcartLinkedDocumentsView,
  entry: PvEcartLinkedSourcesItem | undefined,
  brId: number
): BonReceptionLinkedWorkflowDisplay {
  let documentOriginal =
    mapPvEcartLinkedSourcesToDocumentOriginalRow(entry) ??
    resolveBonReceptionDocumentOriginalListRow(view, brId);
  if (documentOriginal) {
    const code = String(documentOriginal.code ?? '').trim();
    if (!code || code === '—') {
      documentOriginal = documentOriginal.link ? documentOriginal : null;
    }
  }
  return {
    documentOriginal,
    bonSortie: resolveBonReceptionBonSortieDisplayRow(view),
  };
}

function loadBonReceptionViaStockTransfertErpRefs$(
  documentService: DocumentService,
  brDoc: unknown,
  brId: number,
  stationId?: number | null
): Observable<PvEcartLinkedDocumentsView | null> {
  return loadBonSortieViaStockTransfertErpRefs$(
    documentService,
    brDoc,
    readStockTransfertNumFromDocument(brDoc),
    stationId
  ).pipe(
    map((view) => {
      if (!view) {
        return null;
      }
      if (
        bonReceptionLinkedViewHasBonSortie(view) ||
        resolveBonReceptionDocumentOriginalListRow(view, brId)
      ) {
        return view;
      }
      return null;
    })
  );
}

function loadBonReceptionLinkedWorkflowClientFallback$(
  documentService: DocumentService,
  brDoc: unknown,
  brId: number,
  lcStockTransfertService?: LogicielCaisseStockTransfertService,
  stationId?: number | null
): Observable<BonReceptionLinkedWorkflowDisplay> {
  return loadBonReceptionLinkedDocuments$(
    documentService,
    brDoc,
    lcStockTransfertService,
    brId
  ).pipe(
    switchMap((view) => {
      const quick = toBonReceptionLinkedWorkflowDisplay(view, undefined, brId);
      if (quick.documentOriginal || quick.bonSortie) {
        return of(quick);
      }
      return loadBonReceptionViaStockTransfertErpRefs$(
        documentService,
        brDoc,
        brId,
        stationId
      ).pipe(
        map(
          (refsView) =>
            toBonReceptionLinkedWorkflowDisplay(refsView ?? view, undefined, brId)
        )
      );
    }),
    catchError(() =>
      loadBonReceptionViaStockTransfertErpRefs$(documentService, brDoc, brId, stationId).pipe(
        map((refsView) =>
          toBonReceptionLinkedWorkflowDisplay(
            refsView ?? {
              transfert: EMPTY_ROW('Document de transfert'),
              bonSortie: EMPTY_ROW('Bon de sortie'),
              bonReception: EMPTY_ROW('Document de réception'),
            },
            undefined,
            brId
          )
        ),
        catchError(() =>
          of({
            documentOriginal: null,
            bonSortie: null,
          })
        )
      )
    )
  );
}

export type BonReceptionLinkedWorkflowBundle = {
  display: BonReceptionLinkedWorkflowDisplay;
  entry?: PvEcartLinkedSourcesItem;
};

function resolveBonReceptionLinkedWorkflowBundle$(
  documentService: DocumentService,
  brDoc: unknown,
  lcStockTransfertService?: LogicielCaisseStockTransfertService,
  options?: { stationId?: number | null }
): Observable<BonReceptionLinkedWorkflowBundle> {
  const raw = (brDoc && typeof brDoc === 'object' ? brDoc : {}) as Record<string, unknown>;
  const full = (raw['fullData'] as Record<string, unknown> | undefined) ?? raw;
  const merged: Record<string, unknown> = { ...full, ...raw };
  if (raw['fullData'] && typeof raw['fullData'] === 'object') {
    Object.assign(merged, raw['fullData'] as Record<string, unknown>);
    merged['id'] = (raw['fullData'] as Record<string, unknown>)['id'] ?? raw['id'];
  }
  const brId = Number(merged['id']);
  if (!Number.isFinite(brId) || brId <= 0) {
    return of({ display: { documentOriginal: null, bonSortie: null } });
  }

  const clientFallback$ = loadBonReceptionLinkedWorkflowClientFallback$(
    documentService,
    merged,
    brId,
    lcStockTransfertService,
    options?.stationId
  ).pipe(map((display) => ({ display, entry: undefined as PvEcartLinkedSourcesItem | undefined })));

  return documentService.postBonReceptionLinkedWorkflow([brId]).pipe(
    switchMap((items) => {
      const entry = items[String(brId)] as PvEcartLinkedSourcesItem | undefined;
      if (bonReceptionLinkedSourcesEntryIsUsable(entry)) {
        const view = buildPvEcartLinkedDocumentsViewFromSourcesItem(entry);
        return of({
          display: toBonReceptionLinkedWorkflowDisplay(view, entry, brId),
          entry,
        });
      }
      return clientFallback$;
    }),
    catchError(() => clientFallback$)
  );
}

/** Bon réception ERP — document original + bon de sortie (popup / fiche, comme liste shared). */
export function loadBonReceptionLinkedWorkflowDisplay$(
  documentService: DocumentService,
  brDoc: unknown,
  lcStockTransfertService?: LogicielCaisseStockTransfertService,
  options?: { stationId?: number | null }
): Observable<BonReceptionLinkedWorkflowDisplay> {
  return resolveBonReceptionLinkedWorkflowBundle$(documentService, brDoc, lcStockTransfertService, options).pipe(
    map((bundle) => bundle.display)
  );
}

/** Tableau popup bon réception : refs workflow + date / montant / statut ERP. */
export function loadBonReceptionDocumentsLiesTableRows$(
  documentService: DocumentService,
  brDoc: unknown,
  lcStockTransfertService?: LogicielCaisseStockTransfertService,
  options?: { stationId?: number | null }
): Observable<BonReceptionDocumentsLiesTableRow[]> {
  return resolveBonReceptionLinkedWorkflowBundle$(
    documentService,
    brDoc,
    lcStockTransfertService,
    options
  ).pipe(
    switchMap(({ display, entry }) => {
      const skeletons = bonReceptionLinkedWorkflowToDocumentsLiesSkeletons(display, entry);
      return enrichDocumentsLieSkeletonRows$(
        documentService,
        skeletons,
        lcStockTransfertService ?? null
      );
    })
  );
}
